/**
 * In-Process Domain Event Bus (Phase 5).
 * Ergänzt window-Events für optionale UI-Listener.
 *
 * High-frequency UI noise (cache/inventory/data_changed) is coalesced within
 * the same microtask — last payload wins. Business facts (sale_completed, …)
 * still deliver immediately so behavior stays the same.
 */

import { DOMAIN_EVENTS } from './domainEventTypes';

export { DOMAIN_EVENTS };

/** @type {Map<string, Set<Function>>} */
const listeners = new Map();

/** Types that fan out many times per user action (Jul-9 event bridge). */
const COALESCE_TYPES = new Set([
  DOMAIN_EVENTS.CACHE_UPDATED,
  DOMAIN_EVENTS.INVENTORY_UPDATED,
  DOMAIN_EVENTS.DATA_CHANGED,
  DOMAIN_EVENTS.PRODUCTS_UPDATED,
  DOMAIN_EVENTS.PURCHASES_UPDATED
]);

/** @type {Map<string, object>} */
const pendingCoalesce = new Map();
let coalesceScheduled = false;

/**
 * @param {string} type
 * @param {Function} handler — (payload) => void
 * @returns {() => void} unsubscribe
 */
export function subscribeDomainEvent(type, handler) {
  if (!type || typeof handler !== 'function') {
    return () => {};
  }
  if (!listeners.has(type)) {
    listeners.set(type, new Set());
  }
  listeners.get(type).add(handler);
  return () => listeners.get(type)?.delete(handler);
}

function deliverDomainEvent(type, payload = {}) {
  const handlers = listeners.get(type);
  if (handlers) {
    for (const fn of handlers) {
      try {
        fn(payload);
      } catch (err) {
        console.warn('[domainEventBus] handler failed:', type, err?.message || err);
      }
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('elkheir:domain-event', {
        detail: { type, payload, timestamp: Date.now() }
      })
    );
  }
}

function flushCoalesced() {
  coalesceScheduled = false;
  const batch = [...pendingCoalesce.entries()];
  pendingCoalesce.clear();
  for (const [type, payload] of batch) {
    deliverDomainEvent(type, payload);
  }
}

/**
 * @param {string} type
 * @param {object} payload
 */
export function publishDomainEvent(type, payload = {}) {
  if (!type) return;

  if (COALESCE_TYPES.has(type)) {
    pendingCoalesce.set(type, payload ?? {});
    if (!coalesceScheduled) {
      coalesceScheduled = true;
      queueMicrotask(flushCoalesced);
    }
    return;
  }

  deliverDomainEvent(type, payload);
}

/** Test helper — flush pending coalesced events immediately. */
export function __flushDomainEventCoalesceForTests() {
  if (pendingCoalesce.size === 0) return;
  flushCoalesced();
}

/** Test helper — clear listeners + pending coalesce. */
export function __resetDomainEventBusForTests() {
  listeners.clear();
  pendingCoalesce.clear();
  coalesceScheduled = false;
}
